import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Box from '@mui/material/Box/Box';
import Button from '@mui/material/Button/Button';
import TextField from '@mui/material/TextField/TextField';
import CircularProgress from '@mui/material/CircularProgress/CircularProgress';
import TableContainer from '@mui/material/TableContainer/TableContainer';
import Table from '@mui/material/Table/Table';
import TableHead from '@mui/material/TableHead/TableHead';
import TableRow from '@mui/material/TableRow/TableRow';
import TableCell from '@mui/material/TableCell/TableCell';
import TableBody from '@mui/material/TableBody/TableBody';
import Link from '@mui/material/Link';
import IconButton from '@mui/material/IconButton/IconButton';
import DoneIcon from '@mui/icons-material/Done';
import DeleteIcon from '@mui/icons-material/Delete';
import { AppStore } from '../../store/applicationState';
import { DeleteCatalog, GetCatalogues, resetCatalogues, SearchCatalogues } from '../../store/catalogues/actions';
import { useSearch } from '../../hooks/useSearch';
import { ICatalog } from '../../store/catalogues/types';
import { GetCart } from '../../store/cart/actions';
import { ROUTES } from '../../utils/routes';
import { WarningModal } from '../../components/modals';

interface Column {
  id: 'id' | 'title' | 'isMain' | 'isPublic' | 'rubric' | 'authorFio' | 'rubrics' | 'delete';
  label: string;
  align?: 'right' | 'left' | 'center';
  width?: string;
  format?: (value: number) => string;
}

const columns: Column[] = [
  { id: 'id', label: '№', align: 'center', width: '6%' },
  { id: 'title', label: 'Заголовок', align: 'left' },
  {
    id: 'authorFio',
    label: 'Редактор',
    align: 'left',
  },
  {
    id: 'isPublic',
    label: 'Публікція',
    align: 'center',
  },
  {
    id: 'rubric',
    label: 'Рубрика',
    align: 'left',
  },
  {
    id: 'rubrics',
    label: 'Додатк. Рубрики',
    align: 'left',
  },
  {
    id: 'delete',
    label: 'Видалити',
    align: 'center',
    width: '8%',
  },
];

type PropsType = {};

export const ListCatalogues: React.FC<PropsType> = () => {
  const [isModal, setIsModal] = React.useState<{ id: number } | null>(null);
  const [catalogues, setCatalogues] = useState<ICatalog[]>([]);
  const { Catalogues, Configurations } = useSelector((store: AppStore) => store);

  const dispatch = useDispatch();

  const { loading, searchId, searchTitle, resetState, handleSearchId, handleSearchTitle } = useSearch({
    SearchData: SearchCatalogues,
    ResetData: resetCatalogues,
  });

  useEffect(() => {
    dispatch(
      GetCatalogues.request({
        data: {
          siteId: Configurations.siteId,
        },
      })
    );
  }, [Configurations, dispatch]);

  useEffect(() => {
    if (Catalogues.catalogues) setCatalogues(Catalogues.catalogues.filter((el) => !el.is_in_cart));
  }, [Catalogues]);

  React.useEffect(() => {
    dispatch(resetCatalogues());
  }, [dispatch]);

  const handleChangeRoute =
    (path = '' as string | number) =>
    () => {
      window.open(ROUTES.routeChange(`list/${path}?site_id=${Configurations.siteId}`));
    };

  const handleRemove = (id: number) => {
    dispatch(
      DeleteCatalog.request({
        id,
        callBack: (success) => {
          if (success) {
            dispatch(
              GetCart.request({
                data: {
                  siteId: Configurations.siteId,
                },
              })
            );
            dispatch(
              GetCatalogues.request({
                data: {
                  siteId: Configurations.siteId,
                },
              })
            );
          }
        },
      })
    );
  };

  return (
    <>
      <Box >
        <Button
          
          variant='contained'
          color='primary'
          onClick={handleChangeRoute('new')}
        >
          Створити довідник
        </Button>
        <TextField
          size='small'
          id='outlined-basic-id'
          label='Пошук по ID'
          variant='outlined'
          value={searchId}
          
          onChange={(event: React.ChangeEvent<{ value: unknown }>) => {
            handleSearchId(event.target.value as string);
          }}
        />
        <TextField
          
          size='small'
          id='outlined-basic-title'
          label='Пошук по заголовку'
          variant='outlined'
          value={searchTitle}
          onChange={(event: React.ChangeEvent<{ value: unknown }>) => {
            handleSearchTitle(event.target.value as string);
          }}
        />
        <Button  variant='contained' color='primary' onClick={resetState}>
          {loading ? <CircularProgress color='inherit' size={25} /> : 'Очистити фільтри'}
        </Button>
      </Box>
      <TableContainer >
        <Table  stickyHeader aria-label='sticky table'>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.id} align={column.align} style={{ width: column?.width || '' }}>
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {catalogues.map((row, i) => {
              return (
                <TableRow style={{ maxHeight: 50 }} hover role='checkbox' tabIndex={-1} key={`${i}-${row.id}`}>
                  <TableCell align='center'>
                    <Link sx={{ cursor: 'pointer' }} onClick={handleChangeRoute(row.id)}>
                      {row.id}
                    </Link>
                  </TableCell>
                  <TableCell >{row.title}</TableCell>
                  <TableCell >{row.authorFio || ''}</TableCell>
                  <TableCell align='center' >
                    {row.isPublic ? <DoneIcon /> : ''}
                  </TableCell>
                  <TableCell >{row.rubricName}</TableCell>
                  <TableCell >
                    {row.rubrics && row.rubrics.length > 0
                      ? row.rubrics.map((rubric, idx) => (
                          <div key={`${i}-${idx}-${rubric.id}`}>
                            <span>{rubric.name}</span>
                            <br />
                          </div>
                        ))
                      : ''}
                  </TableCell>
                  <TableCell align='center'>
                    <IconButton aria-label='delete' onClick={() => setIsModal({ id: row.id })}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {isModal ? (
        <WarningModal
          handleApprove={(isApprove) => {
            isApprove && handleRemove(isModal.id);
            setIsModal(null);
          }}
          value={`№ ${isModal.id}`}
          open={!!isModal}
        />
      ) : null}
    </>
  );
};
